import { useEffect, useState } from "react";
import { ops, asList, OPS } from "@/admin/api";
import { StatCard, PageTitle, EmptyState, Pill } from "@/admin/ui";

export default function Dashboard() {
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const load = async (key, path) => {
      try {
        const result = await ops(path);
        setData((d) => ({ ...d, [key]: result }));
      } catch (e) {
        setErrors((er) => ({ ...er, [key]: e.message }));
      }
    };
    load("users", OPS.users);
    load("convoys", OPS.convoys);
    load("clubs", OPS.clubs);
    load("sos", OPS.sos);
  }, []);

  const users = asList(data.users, "users");
  const convoys = asList(data.convoys, "convoys");
  const clubs = asList(data.clubs, "clubs");
  const sos = asList(data.sos, "alerts", "sos", "events").filter((a) => !a.resolved);
  const online = users.filter((u) => u.presence?.is_online || u.online === true);
  const premium = users.filter((u) => u.tier === "premium" || u.premium);
  const val = (n, err, loaded) => (loaded ? n : err ? "—" : "…");

  return (
    <div data-testid="admin-dashboard">
      <PageTitle eyebrow="Admin Portal" title="Dashboard" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard label="Registered users" value={val(users.length, errors.users, !!data.users)} sub={errors.users} testId="stat-total-users" />
        <StatCard label="Online now" value={val(online.length, errors.users, !!data.users)} sub={errors.users} accent="text-[#00F0FF]" testId="stat-online-users" />
        <StatCard label="Active convoys" value={val(convoys.length, errors.convoys, !!data.convoys)} sub={errors.convoys} accent="text-[#00F0FF]" testId="stat-active-convoys" />
        <StatCard label="Premium subscribers" value={val(premium.length, errors.users, !!data.users)} sub={errors.users} accent="text-amber-300" testId="stat-premium-subs" />
        <StatCard label="Unresolved SOS" value={val(sos.length, errors.sos, !!data.sos)} sub={errors.sos} accent="text-[#FF3333]" testId="stat-unresolved-sos" />
      </div>

      <h2 className="label-tech mt-10 text-neutral-500">Clubs live on the platform</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {clubs.slice(0, 6).map((c) => (
          <div key={c.id} className="rounded-xl border border-white/10 bg-[#0c0c0e] p-5" data-testid={`dashboard-club-${c.id}`}>
            <div className="flex items-center justify-between gap-3">
              <p className="font-display text-base font-bold text-white">{c.name}</p>
              {c.verified && <Pill tone="cyan">Verified</Pill>}
            </div>
            <p className="mt-1 text-xs text-neutral-500">
              {c.area} · {c.memberCount ?? 0} members · {c.followerCount ?? 0} followers
            </p>
          </div>
        ))}
      </div>
      {!clubs.length && (
        <EmptyState title="No clubs returned" hint={errors.clubs || "The clubs endpoint returned an empty list."} />
      )}
      {Object.keys(errors).length > 0 && (
        <p className="mt-8 max-w-2xl text-xs leading-relaxed text-neutral-600" data-testid="dashboard-unavailable-note">
          Some stats are unavailable because the JomKonvoi backend has not exposed those admin
          endpoints yet. Sections marked "—" will populate automatically once the API exists.
        </p>
      )}
    </div>
  );
}
